import { Button } from '../../../components/ui/button';
import { Camera, Clock, ExternalLink, Gift, Heart, MapPin } from 'lucide-react';
import brandMark from '../../../assets/virei-noiva-mark.svg';

const sectionSpacing = 'py-16 px-6';

export function SectionHeading({ icon: Icon, title, subtitle, headingFont, titleColor, secondaryColor, subtitleColor }) {
  return (
    <div className="text-center mb-10">
      {Icon ? <Icon className="w-10 h-10 mx-auto mb-3" style={{ color: secondaryColor }} /> : null}
      <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: headingFont, color: titleColor }}>
        {title}
      </h2>
      {subtitle ? <p className="mt-2 text-sm" style={{ color: subtitleColor || `${titleColor}B3` }}>{subtitle}</p> : null}
    </div>
  );
}

export function CountdownInline({ daysUntil, textColor }) {
  return (
    <p className="text-4xl md:text-5xl font-light tracking-tight" style={{ color: textColor }}>
      {daysUntil} <span className="text-xl">dias</span>
    </p>
  );
}

export function StoryBlock({ title, storyText, background, titleColor, bodyColor, titleFont, bodyFont, compact = false }) {
  if (!storyText) return null;
  return (
    <section className={sectionSpacing} style={{ background }}>
      <div className={`mx-auto ${compact ? 'max-w-2xl' : 'max-w-4xl'}`}>
        <h2 className="text-3xl md:text-4xl font-bold text-center mb-6" style={{ color: titleColor, fontFamily: titleFont }}>
          {title}
        </h2>
        <p className="whitespace-pre-line leading-relaxed text-center text-lg" style={{ color: bodyColor, fontFamily: bodyFont }}>
          {storyText}
        </p>
      </div>
    </section>
  );
}

export function GalleryBlock({
  galleryImages,
  headingFont,
  titleColor,
  secondaryColor,
  sectionBackground = '#FFFFFF',
  onOpenGallery,
  cardClass = 'rounded-2xl'
}) {
  if (!galleryImages || galleryImages.length === 0) return null;
  return (
    <section className={sectionSpacing} style={{ background: sectionBackground }}>
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          icon={Camera}
          title="Nossa Galeria"
          headingFont={headingFont}
          titleColor={titleColor}
          secondaryColor={secondaryColor}
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {galleryImages.map((img, idx) => (
            <button
              key={idx}
              onClick={() => onOpenGallery?.(idx)}
              className={`aspect-square overflow-hidden ${cardClass} border border-slate-100 hover:opacity-90 transition-opacity`}
            >
              <img src={img} alt={`Gallery ${idx + 1}`} className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

export function LocationBlock({
  locationCards,
  headingFont,
  titleColor,
  textColor,
  secondaryColor,
  titleFont,
  textFont,
  cardClass = 'rounded-2xl border border-slate-100 bg-white shadow-sm',
  title = 'Localização',
  sectionBackground = '#F8F6F4',
  sectionIconColor,
  cardBackground,
  cardIconBackground,
  cardIconColor = '#FFFFFF',
  cardTitleColor,
  cardLinkColor
}) {
  if (!locationCards || locationCards.length === 0) return null;
  return (
    <section className={sectionSpacing} style={{ background: sectionBackground }}>
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          icon={MapPin}
          title={title}
          headingFont={titleFont || headingFont}
          titleColor={titleColor}
          secondaryColor={sectionIconColor || titleColor || secondaryColor}
        />
        <div className={`grid gap-6 ${locationCards.length > 1 ? 'md:grid-cols-2' : 'md:grid-cols-1 max-w-3xl mx-auto'}`}>
          {locationCards.map((item) => {
            const Icon = item.icon;
            return (
              <div
                key={item.key}
                className={cardClass}
                style={cardBackground ? { background: cardBackground } : undefined}
              >
                <div className="p-6 text-center">
                  <div
                    className="w-14 h-14 rounded-full mx-auto mb-4 flex items-center justify-center"
                    style={{ background: cardIconBackground || item.iconBg || secondaryColor }}
                  >
                    <Icon className="w-7 h-7" style={{ color: cardIconColor }} />
                  </div>
                  <h3 className="text-xl font-bold" style={{ color: cardTitleColor || titleColor, fontFamily: titleFont || headingFont }}>{item.title}</h3>
                  {item.location ? <p className="mt-2 font-medium" style={{ color: textColor, fontFamily: textFont }}>{item.location}</p> : null}
                  {item.address ? <p className="text-sm mt-1" style={{ color: textColor, fontFamily: textFont }}>{item.address}</p> : null}
                  {item.time ? (
                    <p className="text-sm mt-2" style={{ color: textColor, fontFamily: textFont }}>
                      <Clock className="w-3 h-3 inline mr-1" />
                      {item.time}
                    </p>
                  ) : null}
                  {item.address ? (
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.address)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 mt-3 text-sm font-medium hover:underline"
                      style={{ color: cardLinkColor || cardTitleColor || titleColor }}
                    >
                      <ExternalLink className="w-4 h-4" />
                      Abrir no Google Maps
                    </a>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

export function GiftsBlock({
  gifts,
  headingFont,
  theme,
  titleColor,
  textColor,
  secondaryColor,
  iconColor,
  sectionBackground,
  cardBackground,
  priceColor,
  buttonBackground,
  buttonText,
  onGiftClick,
  compact = false,
  showGiftSection = true
}) {
  if (!showGiftSection || !gifts || gifts.length === 0) return null;
  const resolvedTitleColor = theme?.giftTitleColor || titleColor;
  const resolvedTextColor = theme?.giftTextColor || textColor;
  const resolvedIconColor = theme?.giftIconColor || iconColor || secondaryColor;
  const resolvedSectionBackground = theme?.giftSectionBackground || sectionBackground || '#FFFFFF';
  const resolvedCardBackground = theme?.giftCardBackground || cardBackground || '#FFFFFF';
  const resolvedPriceColor = theme?.giftPriceColor || priceColor || secondaryColor;
  const resolvedButtonBackground = theme?.giftButtonBackground || buttonBackground || resolvedTitleColor;
  const resolvedButtonText = theme?.giftButtonText || buttonText || '#FFFFFF';

  return (
    <section className={sectionSpacing} style={{ background: resolvedSectionBackground }}>
      <div className="max-w-6xl mx-auto">
        <SectionHeading
          icon={Gift}
          title="Lista de Presentes"
          subtitle="Escolha um presente especial para os noivos"
          headingFont={headingFont}
          titleColor={resolvedTitleColor}
          secondaryColor={resolvedIconColor}
          subtitleColor={resolvedTextColor}
        />
        <div className={`grid gap-5 ${compact ? 'md:grid-cols-2' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
          {gifts.map((gift) => (
            <div
              key={gift.gift_id || gift.name}
              className="rounded-2xl border border-slate-100 overflow-hidden shadow-sm"
              style={{ background: resolvedCardBackground }}
            >
              {gift.image_url ? <img src={gift.image_url} alt={gift.name} className="w-full h-44 object-cover" /> : null}
              <div className="p-5">
                <h3 className="text-lg font-bold" style={{ color: resolvedTitleColor }}>{gift.name}</h3>
                {gift.description ? <p className="text-sm mt-1 line-clamp-2" style={{ color: resolvedTextColor }}>{gift.description}</p> : null}
                <div className="mt-4 flex items-center justify-between">
                  <span className="font-bold text-lg" style={{ color: resolvedPriceColor }}>R$ {Number(gift.price || 0).toFixed(2)}</span>
                  <Button
                    onClick={() => onGiftClick?.(gift)}
                    className="rounded-full px-5"
                    style={{ background: resolvedButtonBackground, color: resolvedButtonText }}
                  >
                    Presentear
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function RsvpBlock({
  onOpenRsvp,
  isTemplatePreview,
  onTemplatePreviewRedirect,
  primaryColor,
  secondaryColor,
  headingFont,
  showRsvpCount = true,
  confirmedGuests = 0,
  sectionBackground,
  buttonBackground,
  buttonText,
  buttonBorder,
  iconColor,
  titleColor,
  bodyColor,
  countColor,
  bodyFont
}) {
  const handleClick = () => {
    if (isTemplatePreview) {
      onTemplatePreviewRedirect?.();
      return;
    }
    onOpenRsvp?.();
  };

  return (
    <section className="py-16 px-6 text-center text-white" style={{ background: sectionBackground || primaryColor }}>
      <Heart className="w-10 h-10 mx-auto mb-4" style={{ color: iconColor || titleColor || secondaryColor }} />
      <h2 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: headingFont, color: titleColor || '#FFFFFF' }}>
        Confirme sua presença
      </h2>
      <p className="mt-3 max-w-md mx-auto" style={{ color: bodyColor || 'rgba(255,255,255,0.8)', fontFamily: bodyFont }}>
        Ficamos muito felizes com a sua presença no nosso grande dia.
      </p>
      <Button
        onClick={handleClick}
        className="mt-6 rounded-full px-10 h-12 font-bold"
        style={{
          background: buttonBackground || secondaryColor,
          color: buttonText || primaryColor,
          borderColor: buttonBorder || secondaryColor,
          borderWidth: 1,
          borderStyle: 'solid'
        }}
      >
        Confirmar presença
      </Button>
      {showRsvpCount && confirmedGuests > 0 ? (
        <p className="mt-4 text-sm" style={{ color: countColor || bodyColor || 'rgba(255,255,255,0.7)', fontFamily: bodyFont }}>{confirmedGuests} pessoa(s) já confirmaram</p>
      ) : null}
    </section>
  );
}

export function SiteFooter({ coupleNames, textColor = '#333333' }) {
  return (
    <footer className="py-8 bg-white text-center">
      <img src={brandMark} alt="Virei Noiva" className="w-6 h-6 mx-auto mb-2 object-contain" />
      <p className="text-sm" style={{ color: textColor }}>{coupleNames}</p>
    </footer>
  );
}

export function renderGalleryByPosition(galleryPosition, desiredPosition, galleryNode) {
  return galleryPosition === desiredPosition ? galleryNode : null;
}

